/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import { useEffect } from 'react';

/**
 * Scrolls the page so the given element is anchored just below the sticky header.
 *
 * @param {object} config
 * @param {string} config.imageElementId - id of the product image element to anchor
 * @param {number} [config.anchorOffset=0] - offset to apply when anchoring the image
 * @param {boolean} [config.shouldAnchor=false] - whether the anchor behavior should run
 * @param {boolean} [config.isMobileDevice=false] - whether the current device is mobile
 */
const useAnchorImageToTop = ({
    imageElementId,
    anchorOffset = 0,
    shouldAnchor = false,
    isMobileDevice = false,
}) => {
    useEffect(() => {
        if (!shouldAnchor || !isMobileDevice) return undefined;

        let lastTarget = -1;
        let debounceTimer;
        let stableTimer;
        let maxTimer;
        let initRetryTimer;
        let observer;

        const scrollToImage = (productImageEl, navEl) => {
            const productTop = productImageEl.getBoundingClientRect().top;
            const navRect = navEl.getBoundingClientRect();
            const effectiveNavBottom = navRect.bottom <= 0 ? 0 : navRect.bottom;
            const target = window.scrollY + productTop - effectiveNavBottom + anchorOffset;
            if (Math.round(target) !== Math.round(lastTarget)) {
                lastTarget = target;
                window.scrollTo({ top: target, behavior: 'smooth' });
            }
        };

        // Re-correct scroll (e.g. PDPDeliveryPopupContainer or Nup async content loading forces layout shift)
        // Retry until both nodes exist
        const MAX_INIT_ATTEMPTS = 10;
        const setupAnchorObserver = (attempt = 0) => {
            const productImageEl = document.getElementById(imageElementId);
            const navEl = document.querySelector('[data-testid="is-sticky-header"]');

            if ((!productImageEl || !navEl) && attempt < MAX_INIT_ATTEMPTS) {
                initRetryTimer = setTimeout(() => setupAnchorObserver(attempt + 1), 100);
                return;
            }

            if (!productImageEl || !navEl || window.scrollY > 0) {
                return;
            }

            observer = new ResizeObserver(() => {
                clearTimeout(debounceTimer);
                clearTimeout(stableTimer);

                debounceTimer = setTimeout(() => scrollToImage(productImageEl, navEl), 100);
            });

            observer.observe(productImageEl.parentElement);
            maxTimer = setTimeout(() => observer.disconnect(), 5000);
        };

        setupAnchorObserver();
        return () => {
            if (observer) {
                observer.disconnect();
            }
            clearTimeout(debounceTimer);
            clearTimeout(stableTimer);
            clearTimeout(maxTimer);
            clearTimeout(initRetryTimer);
        };
    }, [anchorOffset, imageElementId, isMobileDevice, shouldAnchor]);
};

export default useAnchorImageToTop;
